import React, { FC, useEffect, useMemo, useState }                  from 'react';
import { v4 as uuid }                                               from 'uuid';
import { Form, Icon }                                               from 'semantic-ui-react';
import { BasicDialog }                                              from '@src/views/components/modals/BasicDialog';
import { BasicDialogContainer }                                     from '@src/views/components/modals/BasicDialogContainer';
import { Align, SemanticUIColor, Size }                             from '@src/types';
import { useCentralCalls }                                          from '@src/rest/CentralCalls';
import type { EditSowPayload }                                      from './ClientOutlineTypes';

const SOW_TABLE            = 'SOWs';
const DOCUMENT_ITEMS_TABLE = 'DocumentItems';
const SOW_DOCUMENT_TYPE_ID = '2c4b307c-d8c9-4eb2-899b-8bc548b38775';

interface AddSowDialogProps {
    clientId: string;
    clientName: string;
    onClose: () => void;
    onSaved?: () => Promise<void> | void;
    editSow?: EditSowPayload;
}

interface ConsultantOption {
    id: string;
    name: string;
}

const getCaseInsensitiveValue = <T = unknown>( obj: unknown, key: string ): T | undefined => {
    if( !obj || typeof obj !== 'object' ) return undefined;
    const record = obj as Record<string, unknown>;
    const foundKey = Object.keys( record ).find( k => k.toLowerCase() === key.toLowerCase() );
    return foundKey ? ( record[ foundKey ] as T ) : undefined;
};

const asOptionalString = ( value: unknown ): string | undefined => {
    if( value == null ) return undefined;
    const normalized = `${ value }`.trim();
    return normalized || undefined;
};

const parseArrayPayload = ( response: unknown ): unknown[] => {
    if( Array.isArray( response ) ) return response;

    const record = response && typeof response === 'object'
        ? response as Record<string, unknown>
        : null;
    const candidates = [ record?.result, record?.Result, record?.data, record?.Data ];
    for( const candidate of candidates ){
        if( Array.isArray( candidate ) ) return candidate;
        if( typeof candidate === 'string' ){
            try {
                const parsed = JSON.parse( candidate );
                if( Array.isArray( parsed ) ) return parsed;
            } catch {
                // ignore malformed JSON wrappers
            }
        }
    }

    if( typeof response === 'string' ){
        try {
            const parsed = JSON.parse( response );
            if( Array.isArray( parsed ) ) return parsed;
        } catch {
            // ignore malformed JSON payload
        }
    }

    return [];
};

const toDateInputValue = ( value?: string ): string => {
    const trimmed = ( value ?? '' ).trim();
    if( !trimmed ) return '';
    return trimmed.split( 'T' )[ 0 ] ?? '';
};

const toBudgetedHoursInput = ( value?: number ): string => {
    if( value == null || !Number.isFinite( value ) ) return '';
    return `${ value }`;
};

const normalizeConsultants = ( consultantRows: unknown[], accountRows: unknown[] ): ConsultantOption[] => {
    const accountsById = new Map<string, unknown>();
    accountRows.forEach( row => {
        const id = asOptionalString( getCaseInsensitiveValue( row, 'Id' ) )?.toLowerCase();
        if( id ) accountsById.set( id, row );
    } );

    return consultantRows
        .map( row => {
            const id = asOptionalString( getCaseInsensitiveValue( row, 'Id' ) );
            if( !id ) return null;

            const accountId = asOptionalString( getCaseInsensitiveValue( row, 'AccountId' ) )?.toLowerCase();
            const account = accountId ? accountsById.get( accountId ) : undefined;
            const firstName = asOptionalString( getCaseInsensitiveValue( account, 'FirstName' ) ) || '';
            const lastName = asOptionalString( getCaseInsensitiveValue( account, 'LastName' ) ) || '';
            const email = asOptionalString(
                getCaseInsensitiveValue( account, 'EmailAddress' )
                ?? getCaseInsensitiveValue( account, 'Email' )
            );
            const name = `${ firstName } ${ lastName }`.trim() || email || 'Consultant';

            return { id, name };
        } )
        .filter( ( row ): row is ConsultantOption => row != null )
        .sort( ( a, b ) => a.name.localeCompare( b.name ) );
};

const AddSowDialog: FC<AddSowDialogProps> = ( { clientId, clientName, onClose, onSaved, editSow } ) => {

    const { RunInsert, RunUpdate, TableSelect } = useCentralCalls();

    const isEditMode = !!editSow?.sowId;

    const [ sowName, setSowName ]                               = useState( editSow?.sowName ?? '' ),
          [ sowStartDate, setSowStartDate ]                     = useState( toDateInputValue( editSow?.startDate ) ),
          [ sowEndDate, setSowEndDate ]                         = useState( toDateInputValue( editSow?.endDate ) ),
          [ budgetedHoursInput, setBudgetedHoursInput ]         = useState( toBudgetedHoursInput( editSow?.budgetedHours ) ),
          [ expansionOpportunity, setExpansionOpportunity ]     = useState( editSow?.expansionOpportunity === true ),
          [ leadConsultantId, setLeadConsultantId ]             = useState( editSow?.sowLeadConsultantId ?? '' ),
          [ consultants, setConsultants ]                       = useState<ConsultantOption[]>( [] ),
          [ isLoadingConsultants, setIsLoadingConsultants ]     = useState( false ),
          [ isSaving, setIsSaving ]                             = useState( false ),
          [ error, setError ]                                   = useState( '' );

    useEffect( () => {
        setSowName( editSow?.sowName ?? '' );
        setSowStartDate( toDateInputValue( editSow?.startDate ) );
        setSowEndDate( toDateInputValue( editSow?.endDate ) );
        setBudgetedHoursInput( toBudgetedHoursInput( editSow?.budgetedHours ) );
        setExpansionOpportunity( editSow?.expansionOpportunity === true );
        setLeadConsultantId( editSow?.sowLeadConsultantId ?? '' );
        setError( '' );
    }, [
        editSow?.sowId,
        editSow?.sowName,
        editSow?.startDate,
        editSow?.endDate,
        editSow?.budgetedHours,
        editSow?.expansionOpportunity,
        editSow?.sowLeadConsultantId,
    ] );

    useEffect( () => {
        let cancelled = false;

        const loadConsultants = async () => {
            setIsLoadingConsultants( true );
            try {
                const [ consultantRows, accountRows ] = await Promise.all( [
                    TableSelect( 'Consultants', {} ),
                    TableSelect( 'Accounts', {} ),
                ] );

                if( cancelled ) return;

                const normalized = normalizeConsultants(
                    parseArrayPayload( consultantRows ),
                    parseArrayPayload( accountRows )
                );
                setConsultants( normalized );

                const preferredId = ( editSow?.sowLeadConsultantId ?? '' ).trim();
                if( preferredId ){
                    const hasPreferred = normalized.some(
                        consultant => consultant.id.toLowerCase() === preferredId.toLowerCase()
                    );
                    if( hasPreferred ){
                        setLeadConsultantId( preferredId );
                        return;
                    }
                }

                const preferredName = ( editSow?.sowLeadConsultant ?? '' ).trim().toLowerCase();
                if( preferredName ){
                    const matched = normalized.find(
                        consultant => consultant.name.trim().toLowerCase() === preferredName
                    );
                    if( matched ) setLeadConsultantId( matched.id );
                }
            } catch( err ){
                console.error( '[AddSow] failed to load consultants:', err );
                if( !cancelled ) setConsultants( [] );
            } finally {
                if( !cancelled ) setIsLoadingConsultants( false );
            }
        };

        loadConsultants();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ editSow?.sowId, editSow?.sowLeadConsultantId, editSow?.sowLeadConsultant ] );

    const consultantOptions = useMemo(
        () => consultants.map( consultant => ( {
            key: consultant.id,
            value: consultant.id,
            text: consultant.name,
        } ) ),
        [ consultants ]
    );

    const handleSubmit = async () => {
        if( isSaving ) return;

        const name = sowName.trim();
        if( !name ){
            setError( 'Enter a SOW name.' );
            return;
        }

        if( !sowStartDate || !sowEndDate ){
            setError( 'Start date and end date are required.' );
            return;
        }

        if( sowStartDate && sowEndDate && sowStartDate > sowEndDate ){
            setError( 'Start date cannot be after end date.' );
            return;
        }

        const trimmedHours = budgetedHoursInput.trim();
        let budgetedHours: number | null = null;
        if( trimmedHours !== '' ){
            const parsedHours = Number( trimmedHours );
            if( !Number.isInteger( parsedHours ) || parsedHours < 0 ){
                setError( 'Budgeted hours must be a whole number of 0 or more.' );
                return;
            }
            budgetedHours = parsedHours;
        }

        const resolvedLeadConsultantId = leadConsultantId.trim() || null;

        setIsSaving( true );
        setError( '' );
        try {
            if( isEditMode && editSow?.sowId ){
                await RunUpdate( SOW_TABLE, editSow.sowId, {
                    Name: name,
                    StartDate: sowStartDate,
                    EndDate: sowEndDate,
                    BudgetedHours: budgetedHours,
                    ExpansionOpportunity: expansionOpportunity,
                    SOWLeadConsultantId: resolvedLeadConsultantId,
                } );
            } else {
                const documentItemId = uuid();

                await RunInsert( DOCUMENT_ITEMS_TABLE, {
                    Id: documentItemId,
                    ClientId: clientId,
                    DocumentTypeId: SOW_DOCUMENT_TYPE_ID,
                    Name: name,
                } );

                await RunInsert( SOW_TABLE, {
                    Id: uuid(),
                    DocumentItemId: documentItemId,
                    ClientId: clientId,
                    Name: name,
                    StartDate: sowStartDate,
                    EndDate: sowEndDate,
                    BudgetedHours: budgetedHours,
                    ExpansionOpportunity: expansionOpportunity,
                    SOWLeadConsultantId: resolvedLeadConsultantId,
                } );
            }

            await onSaved?.();
            onClose();
        } catch( err ){
            console.error( '[AddSow] failed to save SOW:', err );
            setError( `Could not ${ isEditMode ? 'update' : 'save' } the SOW. Check the console for details.` );
        } finally {
            setIsSaving( false );
        }
    };

    return (
        <BasicDialog
            showCancelButton
            showSubmitButton
            submitLabel={ isSaving ? 'Saving...' : ( isEditMode ? 'Update SOW' : 'Add SOW' ) }
            submitColor={ SemanticUIColor.PURPLE }
            onCancel={ onClose }
            onSubmit={ handleSubmit }
            controlAlign={ Align.CENTER }
            className={ 'no-padding create-client-dialog' }
        >
            <BasicDialogContainer
                title={ `${ isEditMode ? 'Edit SOW' : 'Add SOW' } — ${ clientName }` }
                titleColor={ SemanticUIColor.PURPLE }
                contentPadding={ [ Size.MD, Size.MD, Size.LG, Size.MD ] }
                contentMinHeight={ '32dvh' }
                onClose={ onClose }
            >
                <Form className={ 'create-client-form' }>
                    { error && (
                        <p className={ 'create-client-error' }>
                            <Icon name={ 'warning circle' }/> { error }
                        </p>
                    ) }

                    <div className={ 'create-client-section' }>
                        <div className={ 'create-client-section-header' }>
                            <span>{ isEditMode ? 'Edit SOW' : 'New SOW' }</span>
                        </div>
                        <p className={ 'assign-consultants-help' }>
                            { isEditMode
                                ? 'Update the SOW details, then click Update SOW.'
                                : 'Enter a SOW name, then click Add SOW.' }
                        </p>
                        <Form.Input
                            fluid
                            label={ 'SOW Name' }
                            placeholder={ 'e.g. Q4 Commercialization Workstream' }
                            value={ sowName }
                            onChange={ ( _, data ) => {
                                setSowName( String( data.value ?? '' ) );
                                if( error ) setError( '' );
                            } }
                            disabled={ isSaving }
                        />
                        <div className={ 'create-client-date-row' }>
                            <Form.Field>
                                <label>Start date</label>
                                <input
                                    type={ 'date' }
                                    value={ sowStartDate }
                                    onChange={ e => {
                                        setSowStartDate( e.target.value );
                                        if( error ) setError( '' );
                                    } }
                                    disabled={ isSaving }
                                />
                            </Form.Field>
                            <Form.Field>
                                <label>End date</label>
                                <input
                                    type={ 'date' }
                                    value={ sowEndDate }
                                    onChange={ e => {
                                        setSowEndDate( e.target.value );
                                        if( error ) setError( '' );
                                    } }
                                    disabled={ isSaving }
                                />
                            </Form.Field>
                        </div>

                        <Form.Select
                            label={ 'Lead consultant' }
                            search
                            clearable
                            options={ consultantOptions }
                            value={ leadConsultantId || undefined }
                            placeholder={
                                isLoadingConsultants
                                    ? 'Loading consultants...'
                                    : consultants.length === 0
                                        ? 'No consultants found'
                                        : 'Select lead consultant'
                            }
                            disabled={ isSaving || isLoadingConsultants || consultants.length === 0 }
                            onChange={ ( _event, data ) => {
                                setLeadConsultantId( String( data.value ?? '' ) );
                                if( error ) setError( '' );
                            } }
                        />

                        <div className={ 'create-client-date-row' }>
                            <Form.Input
                                label={ 'Budgeted hours' }
                                type={ 'number' }
                                min={ 0 }
                                step={ 1 }
                                inputMode={ 'numeric' }
                                value={ budgetedHoursInput }
                                placeholder={ 'e.g. 250' }
                                disabled={ isSaving }
                                onChange={ ( _event, data ) => {
                                    setBudgetedHoursInput( String( data.value ?? '' ) );
                                    if( error ) setError( '' );
                                } }
                            />

                            <Form.Field>
                                <label>Expansion opportunity</label>
                                <Form.Checkbox
                                    toggle
                                    label={ expansionOpportunity ? 'Yes' : 'No' }
                                    checked={ expansionOpportunity }
                                    disabled={ isSaving }
                                    onChange={ () => {
                                        setExpansionOpportunity( prev => !prev );
                                        if( error ) setError( '' );
                                    } }
                                />
                            </Form.Field>
                        </div>
                    </div>
                </Form>
            </BasicDialogContainer>
        </BasicDialog>
    );
};

export default AddSowDialog;
